import type { NotificationOverviewPrimaryContactRecord } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { invalidateAfterUserGroupMemberMutation, noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorMessageInternal, noddiToast, useAppForm, usePagination } from 'noddi-ui-web';
import { useEffect } from 'react';
import { z } from 'zod';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';

type Props = {
  userGroupId: number;
  primaryContact: NotificationOverviewPrimaryContactRecord | null;
  translations: NotificationGroupOverviewTranslations;
  showMutationToasts: boolean;
};

const memberName = (member: { firstName: string; lastName: string }) => `${member.firstName} ${member.lastName}`.trim();

/** The organization's one primary contact. Sits above the cars and members tabs. */
export const OrganizationPrimaryContactForm = ({
  userGroupId,
  primaryContact,
  translations: t,
  showMutationToasts
}: Props) => {
  const { pageIndex, pageSize, search, setSearch } = usePagination();
  const {
    data: members,
    isFetching,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedNotificationOverviewMembers,
    input: { userGroupId, pageIndex, pageSize, search },
    queryConfig: { placeholderData: keepPreviousData }
  });
  const options = (members?.results ?? []).map((member) => ({
    value: member.userGroupMemberId,
    label: memberName(member)
  }));
  const schema = z
    .object({
      member: z.union([z.object({ value: z.number(), label: z.string() }), z.undefined()])
    })
    .superRefine((value, context) => {
      if (value.member === undefined) {
        context.addIssue({ code: 'custom', message: t.chooseMember, path: ['member'] });
      }
    });

  const { mutateAsync: patchMember } = noddiAsync.usePatch({
    type: URLKeys.patchUserGroupMember,
    queryConfig: {
      meta: { suppressSuccessToast: showMutationToasts },
      onSuccess: () => invalidateAfterUserGroupMemberMutation({ userGroupId })
    }
  });

  useEffect(() => {
    if (window.location.hash !== '#primary-contact') {
      return;
    }
    document.getElementById('primary-contact')?.scrollIntoView({ block: 'start' });
  }, []);

  const form = useAppForm({
    defaultValues: {
      member: primaryContact
        ? { value: primaryContact.userGroupMemberId, label: memberName(primaryContact) }
        : undefined
    },
    validators: { onSubmit: schema },
    onSubmit: async ({ value }) => {
      const member = value.member;
      if (!member || member.value === primaryContact?.userGroupMemberId) {
        return;
      }
      await patchMember({ userGroupMemberId: member.value, isPrimaryContact: true });
      if (showMutationToasts) {
        noddiToast.success(t.primaryContactSaved(member.label));
      }
    }
  });

  return (
    <section id='primary-contact' className='flex flex-col gap-3 border-b border-border pb-4'>
      <div className='flex flex-col gap-1'>
        <h3 className='text-lg font-semibold'>{t.primaryContactTitle}</h3>
        <p className='max-w-2xl text-sm text-muted-foreground'>{t.primaryContactDescription}</p>
      </div>
      <form.AppForm>
        <form
          className='flex flex-col gap-3 sm:flex-row sm:items-end'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <div className='min-w-0 flex-1'>
            {error && <ApiErrorMessageInternal error={error} />}
            <form.AppField name='member'>
              {(field) => (
                <field.AsyncSelect
                  label={t.member}
                  placeholder={t.chooseMember}
                  searchPlaceholder={t.searchMembersPlaceholder}
                  emptyMessage={t.noMembersFound}
                  options={options}
                  isLoading={isFetching}
                  clearable={false}
                  onSearch={setSearch}
                />
              )}
            </form.AppField>
          </div>
          <form.SubscribeButton label={t.saveChanges} className='shrink-0' />
        </form>
      </form.AppForm>
    </section>
  );
};
